import { toast } from 'sonner';

import { registerActionHandler } from '../action-registry';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';
import { buildSmallAppUrl } from '../smallapp-url';
import { dispatchPreviewRefetch } from '../preview-refetch-event';

/**
 * `testSharePointConnection` — proves the SharePoint credentials AND site access before
 * the asset is saved.
 *
 * ⛔ HANDLER mode, not `action.endpoint`. `smallappProxyAction` requires an
 * already-saved asset (`findOne({_id: assetId})` → Forbidden), so an endpoint action
 * cannot run on CREATE — which is precisely when the user needs this feedback. Same
 * approach as `resolve-drive-url`: POST directly to `{appMeta.apiUrl}/asset/validate`
 * with the Firebase/share-link REST token.
 *
 * Why this handler earns its place: with SharePoint, authentication and site ACCESS are
 * granted separately. A Service Principal can authenticate perfectly and then 403 on
 * every Graph call because an admin never granted it that site — and under the
 * production `Sites.Selected` posture that is the LIKELY outcome, not an edge case.
 * Without this gate the customer saves a config that looks fine and gets a blank screen.
 *
 * Triggers:
 *  - `manual` (button click) — toasts success or failure.
 *  - `isSaveCheck` (runOnSave) — silent on success; returns `{ ok: false, reason }` to
 *    block the save with the backend's customer-facing message.
 *
 * WARNINGS are not toasted on either path. They describe a lasting state of the config
 * ("every post is older than Max Age"), so they go into `_connectionWarning` and render
 * as an inline banner that persists until the state changes — see the write below.
 *
 * The backend answers 200 with `{status:false,message}` for a failure (rather than a
 * non-2xx) so our copy surfaces instead of a generic network error.
 */
registerActionHandler(
  'testSharePointConnection',
  async ({ values, form, appMeta, apolloClient, t, isSaveCheck, trigger }) => {
    const quiet = !!isSaveCheck || (trigger != null && trigger !== 'manual');
    const apiUrl = appMeta?.apiUrl;

    if (!apiUrl) {
      // Misconfigured AppMeta — an ops problem, not the user's. Never block a save on it.
      if (!quiet) toast.error(t('This app is not configured for connection testing'));
      return;
    }

    const siteUrl = String((values as Record<string, unknown>).siteUrl ?? '').trim();
    if (!siteUrl) {
      if (!quiet) toast.error(t('Please enter a SharePoint site URL first'));
      return { ok: false, reason: t('Please enter a SharePoint site URL') };
    }

    try {
      // Share-link-aware: on /share/* pages this refreshes the Firebase token so it
      // carries a fresh shareLinkObjectId claim, otherwise a cross-company user's real
      // accountId would leak into the microservice.
      const token = await getSmallAppRestToken(apolloClient);
      const response = await fetch(buildSmallAppUrl(apiUrl, 'asset/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Send the whole form: the backend needs siteUrl plus whichever auth group is
        // populated, and it honours `enabledIntegration` to pick the mode rather than
        // guessing from a stale key left in form state.
        body: JSON.stringify(values),
      });

      const data = (await response.json()) as {
        status?: boolean;
        message?: string;
        warning?: string;
        /** Success detail — how many posts will actually render. */
        asset?: { siteId?: string; siteName?: string };
      };

      if (!data?.status) {
        const reason =
          data?.message ||
          t('Could not connect to that SharePoint site. Please check the URL and credentials.');
        if (!quiet) toast.error(reason);
        // Drop any previous warning banner: it described a site we could still reach,
        // and leaving it under a hard connection error tells two conflicting stories.
        form?.setValue('_connectionWarning', '', { shouldDirty: false });
        // Block the save — this is the whole point of runOnSave here.
        return { ok: false, reason };
      }

      // Absorb the resolved ids so the sync does not have to re-resolve them, and so a
      // saved asset carries the durable composite site key.
      if (data.asset?.siteId && form) {
        form.setValue('siteId', data.asset.siteId, { shouldDirty: true });
        if (data.asset.siteName) {
          form.setValue('siteName', data.asset.siteName, { shouldDirty: true });
        }
      }

      // A connected-but-empty site is VALID (a site can be configured before its first
      // post is published), so it warns and still allows the save.
      //
      // ⛔ INLINE, not a toast. What the backend is reporting here is a persistent state
      // of the configuration — every post is older than Max Age, or the site publishes no
      // news yet — and the screen will render nothing for as long as it holds. A toast
      // says that once and vanishes, leaving a form that looks correct beside a blank
      // screen, with no way to recall the message except pressing Test Connection again.
      // Worse, the save path runs this handler with `quiet`, so the toast never appeared
      // on the journey where it matters most.
      //
      // The write is deliberately NOT gated on `quiet`: the banner is state, not a
      // notification, so it must appear whether the check ran from the button or from
      // Save. `shouldDirty: false` because the handler is describing the form, not
      // editing it — dirtying here would make a bare Test Connection look like an unsaved
      // change (and `_`-prefixed keys never persist anyway).
      form?.setValue('_connectionWarning', data.warning ?? '', { shouldDirty: false });

      // The connection is good, so the backend feed may hold posts the preview pane has not
      // fetched yet — SharePoint News crawls asynchronously and can take hours, and the pane
      // otherwise only refetches on open or when a dep changes. A successful test is the natural
      // moment to re-read it, so signal the open preview to refetch without a dialog reopen.
      dispatchPreviewRefetch();

      if (data.warning) return { ok: true };

      if (!quiet) {
        // ⛔ Say how MUCH will render, not just that we reached the site. "Connected to
        // Demo01" was equally true for a site whose every post the Max Age filter then
        // discarded — the screen went on to show a bare "No news posts to show yet." and
        // nothing in the product connected the two. The backend counts posts BOTH
        // unfiltered and within the age window precisely so this sentence can be
        // specific.
        const site = data.asset?.siteName
          ? // `t` takes a single key -- interpolate after resolving, as
            // OAuthConnectField does for "Authorized as {{name}}".
            t('Connected to {{site}}').replace('{{site}}', data.asset.siteName)
          : t('Connection successful');
        toast.success(site);
      }
      return { ok: true };
    } catch {
      const reason = t('Could not reach the SharePoint service. Please try again.');
      if (!quiet) toast.error(reason);
      // A transport failure must NOT silently block a save forever, but it also must not
      // pretend the connection is fine — surface it and let the user retry.
      return { ok: false, reason };
    }
  },
);
